import type { Canvas, StaticCanvas } from 'fabric';

import { isDugout } from '@/config/features';
import readDugoutParams from '@/utils/readDugoutParams';

const exportData = (
  canvas: Canvas | StaticCanvas,
  shouldAutoImport: boolean,
  iconType: string,
  fieldDimensions: unknown,
  parentUrl: string,
  fieldVariation: string | undefined,
  // The resolved theme, appended last so existing positional callers are unaffected.
  theme: string,
): void => {
  canvas.includeDefaultValues = false; // this reducees the JSON output size
  const serializedJSON = canvas.toDatalessObject([
    // need to specify what options we want fabric to serialize
    '_controlsVisibility',
    'objectCaching',
    'selectable',
    'hasBorders',
    'hasControls',
    'defaultIcon',
    'alternateIcon',
    'id',
    'name',
    'color',
    'variation',
  ]) as Record<string, unknown>;

  serializedJSON.iconType = iconType;
  serializedJSON.fieldDimensions = fieldDimensions;
  serializedJSON.fieldVariation = fieldVariation;

  const drawingJson = JSON.stringify(serializedJSON);

  // Dugout hands the drawing back to the Moodle host as {action:'datacallback', data:{…}} — the exact
  // shape drawingtool2.js.processData reads (data.data.drawing_JSON + data.data.img_data; user/
  // question/attempt are echoed so the host can correlate the save). img_data is a client-side PNG
  // snapshot, replacing the legacy save_png.php round-trip — its temp_png field is dropped (the host
  // treats it as optional). Every other theme keeps the rinus {type:'save', data:{JSON, SVG}} contract
  // byte-for-byte. Both branches are live in a single build, since the theme is resolved per request.
  const dugoutParams = isDugout(theme) ? readDugoutParams() : null;
  const returnValues = dugoutParams
    ? {
        action: 'datacallback',
        data: {
          attempt: dugoutParams.attemptid,
          user: dugoutParams.userid,
          question: dugoutParams.questionid,
          drawing_JSON: drawingJson,
          img_data: canvas.toDataURL({
            format: 'png',
            multiplier: 1,
            enableRetinaScaling: true,
            left: 0,
            top: 0,
            width: canvas.width,
            height: canvas.height,
          }),
        },
      }
    : {
        type: 'save',
        data: {
          drawing_JSON: drawingJson,
          drawing_SVG: encodeURIComponent(canvas.toSVG()),
        },
      };

  if (window.parent) window.parent.postMessage(returnValues, parentUrl);
};

export default exportData;
